import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Loader2, Upload, FileText, X, ZoomIn, ZoomOut, RotateCcw, Download } from 'lucide-react';

/**
 * Reusable Floor Plan manager: upload / view / download / delete + zoomable PDF preview.
 * Used by Electrical Systems and Communication Systems General Info.
 *
 * Props:
 *  - apiBase: API path prefix (e.g. '/electrical-systems', '/communication-systems')
 *  - projectId
 *  - floorPlan: current floor plan object (or null)
 *  - onChange: (floorPlanOrNull) => void  — called after upload/delete
 *  - editMode: boolean — shows upload area & delete control
 */
export const FloorPlanManager = ({ apiBase, projectId, floorPlan, onChange, editMode }) => {
  const [uploading, setUploading] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files are accepted'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}${apiBase}/${projectId}/general-info/floor-plan`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange?.(res.data);
      toast.success('Floor plan uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload floor plan');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePlan = async () => {
    setDeletingPlan(true);
    try {
      await axios.delete(`${API}${apiBase}/${projectId}/general-info/floor-plan`);
      onChange?.(null);
      toast.success('Floor plan removed');
    } catch { toast.error('Failed to remove floor plan'); }
    finally { setDeletingPlan(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.get(`${API}${apiBase}/${projectId}/general-info/floor-plan/download-url`);
      const resp = await fetch(data.url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = data.fileName || floorPlan?.fileName || 'floor-plan.pdf';
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download floor plan');
    } finally { setDownloading(false); }
  };

  if (floorPlan) {
    return (
      <div className="space-y-3">
        {/* File info bar */}
        <div className="flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg" data-testid="floor-plan-file">
          <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-700 truncate flex-1">{floorPlan.fileName}</span>
          <button
            disabled={downloading}
            onClick={handleDownload}
            className="text-xs text-blue-600 hover:underline flex items-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="floor-plan-download"
          >
            {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            {downloading ? 'Downloading...' : 'Download'}
          </button>
          {editMode && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={handleDeletePlan} disabled={deletingPlan} data-testid="floor-plan-delete">
              {deletingPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            </Button>
          )}
        </div>
        {/* PDF Preview with zoom */}
        <FloorPlanPreview
          key={floorPlan.previewUrl || floorPlan.fileUrl || floorPlan.fileName}
          floorPlan={floorPlan}
          apiBase={apiBase}
          projectId={projectId}
        />
      </div>
    );
  }

  if (editMode) {
    return (
      <div className="border-2 border-dashed rounded-lg p-6 text-center border-slate-200 hover:border-slate-300 cursor-pointer transition-colors" onClick={() => fileRef.current?.click()} data-testid="floor-plan-upload-area">
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={handleFileUpload} />
        {uploading ? (
          <Loader2 className="h-8 w-8 mx-auto text-orange-500 animate-spin" />
        ) : (
          <>
            <Upload className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Click to upload floor plan (PDF only)</p>
          </>
        )}
      </div>
    );
  }

  return <p className="text-sm text-slate-400 italic">No floor plan uploaded</p>;
};

/* ── FloorPlanPreview: renders PDF first page via Cloudinary image conversion ── */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const FloorPlanPreview = ({ floorPlan, apiBase, projectId }) => {
  const [zoom, setZoom] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);

  // Cloudinary converts PDF to image when extension changed to .jpg
  const imageUrl = floorPlan.previewUrl || floorPlan.fileUrl?.replace(/\.pdf$/i, '.jpg') || '';

  const handleWheel = useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom(z => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP))));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  if (!imageUrl) return null;

  return (
    <div className="space-y-2" data-testid="floor-plan-preview">
      {/* Zoom controls */}
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))} disabled={zoom <= MIN_ZOOM} data-testid="floor-plan-zoom-out">
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <span className="text-xs text-slate-500 w-12 text-center font-medium">{Math.round(zoom * 100)}%</span>
        <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))} disabled={zoom >= MAX_ZOOM} data-testid="floor-plan-zoom-in">
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        {zoom !== 1 && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-400" onClick={() => setZoom(1)} data-testid="floor-plan-zoom-reset">
            <RotateCcw className="h-3 w-3 mr-1" />Reset
          </Button>
        )}
      </div>
      {/* Preview area */}
      <div
        ref={containerRef}
        className="relative overflow-auto rounded-lg border border-slate-200 bg-slate-100"
        style={{ maxHeight: 480, cursor: zoom > 1 ? 'grab' : 'default' }}
        data-testid="floor-plan-preview-area"
      >
        {!loaded && !error && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-40 text-sm text-slate-400">
            Could not render preview.{' '}
            <button
              className="ml-1 text-blue-600 hover:underline cursor-pointer"
              onClick={async () => {
                try {
                  const { data } = await axios.get(`${API}${apiBase}/${projectId}/general-info/floor-plan/download-url`);
                  window.open(data.url, '_blank');
                } catch { toast.error('Failed to open floor plan'); }
              }}
            >Open PDF</button>
          </div>
        )}
        <img
          src={imageUrl}
          alt="Floor Plan"
          className={`block mx-auto transition-transform duration-150 ${!loaded ? 'hidden' : ''}`}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: zoom > 1 ? undefined : '100%' }}
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      </div>
      <p className="text-[10px] text-slate-400">Ctrl + Scroll to zoom</p>
    </div>
  );
};
